/**
 * Currency formatting — the ONE definition.
 *
 * ── Why it lives in `models` and not in `common` ────────────────────────────
 * It used to live only in `packages/common/src/utils/utils.ts`, which is the
 * shadcn/Radix/recharts component package. That is fine for an app importing a
 * component library anyway, and wrong for anything that needs four lines of
 * `Intl` without the component graph: `@order-up/common` compiles to CommonJS
 * and does not tree-shake, so one formatter import drags the whole barrel in.
 *
 * That cost is real for `packages/board-renderer`, whose built bundle is
 * `go:embed`-ed into the gateway binary and served from loopback on the box
 * that is also the store's PCI enforcement point. The renderer briefly carried
 * its own copy for exactly that reason — which is the copied-helper drift class
 * this repo has been bitten by before.
 *
 * `models` is the right home: it has no dependencies, `common` already depends
 * on it, and the price DISPLAY projection (`menuPriceDisplay.ts`) already lives
 * here. `common` now re-exports these, so every existing
 * `import { formatPrice } from '@order-up/common'` keeps working unchanged and
 * there is still exactly one implementation.
 *
 * ── Why the formatter is memoised ───────────────────────────────────────────
 * Constructing an `Intl.NumberFormat` is expensive relative to formatting with
 * one, and a menu board re-formats every line on every menu push. One formatter
 * per currency, built once, is the difference between a snappy repaint and a
 * visible stall on appliance-class hardware. Every other caller gets the same
 * win for free.
 *
 * The `en-US` locale is deliberate and unchanged from the original: it fixes
 * grouping and decimal separators while `currency` chooses the symbol. Deriving
 * the locale from the environment would make the same price render differently
 * on a manager's laptop and on the wall.
 */

const FORMATTERS = new Map<string, Intl.NumberFormat>();

function formatterFor(currency: string): Intl.NumberFormat {
  const cached = FORMATTERS.get(currency);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
  FORMATTERS.set(currency, formatter);
  return formatter;
}

/**
 * Format a price for display: `$10.50`.
 *
 * @param currency ISO 4217 code. Defaults to USD — the last-resort default for
 *                 a caller with no restaurant context. Surfaces that HAVE the
 *                 answer (`IMenuSnapshot.currency`) must pass it; guessing from
 *                 a locale would silently relabel every price.
 */
export function formatPrice(amount: number, currency = 'USD'): string {
  return formatterFor(currency).format(amount);
}

/** Alias for {@link formatPrice}, kept for the call sites that read better. */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return formatPrice(amount, currency);
}
